import { useState } from "react";
import { useOutletContext } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Share2 } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Modal } from "@/components/ui/modal";
import { ShareChildDialog } from "@/features/sharing/components/share-child-dialog";
import { AccessList } from "@/features/sharing/components/access-list";
import { SchoolIepsCard } from "@/features/iep-versions/components/school-ieps-card";
import { AboutMyChildCard } from "@/features/contributions/components/about-my-child-card";
import { JournalCard } from "@/features/journal/components/journal-card";
import { SharedDraftsCard } from "@/features/shared-drafts/components/shared-drafts-card";
import { ChildProgressCard } from "@/features/goals/components/child-progress-card";
import { UpcomingMeetingCard } from "./upcoming-meeting-card";
import { InviteStudentForm } from "@/features/student/components/invite-student-form";
import { inviteStudentFromParent } from "@/features/student/api/student-invite-api";
import { gradeLevelLabel } from "@/lib/grade-level-label";
import { disabilityCategoryLabel } from "@/lib/disability-category-label";
import { formatDate } from "@/lib/format-date";
import type { ChildOutletContext } from "./child-detail-page";

export function ChildOverviewTab() {
  const { t } = useTranslation("children");
  const { child, childId } = useOutletContext<ChildOutletContext>();
  const [showShareDialog, setShowShareDialog] = useState(false);
  const [showInviteStudent, setShowInviteStudent] = useState(false);
  const [accessListKey, setAccessListKey] = useState(0);
  const isOwner = child.role === "owner";
  const canEdit = isOwner || child.role === "collaborator";

  const handleInviteStudent = async (email: string) => {
    try {
      const response = await inviteStudentFromParent(childId, email);
      return { success: response.success, message: response.message };
    } catch {
      return {
        success: false,
        message: t("overview.inviteStudentError"),
      };
    }
  };

  return (
    <div className="space-y-6">
      <Card data-testid="child-profile-section">
        <h2 className="font-serif mb-4">{t("overview.profileHeading")}</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {child.dateOfBirth && (
            <ProfileField
              label={t("fields.dateOfBirth")}
              value={formatDate(child.dateOfBirth)}
            />
          )}
          {child.gradeLevel && (
            <ProfileField label={t("fields.gradeLevel")} value={gradeLevelLabel(child.gradeLevel)} />
          )}
          {child.disabilityCategory && (
            <ProfileField
              label={t("fields.disabilityCategory")}
              value={disabilityCategoryLabel(child.disabilityCategory)}
            />
          )}
          {child.schoolDistrict && (
            <ProfileField
              label={t("fields.schoolDistrict")}
              value={child.schoolDistrict}
            />
          )}
        </div>
      </Card>

      <UpcomingMeetingCard childId={childId} />

      <SharedDraftsCard childId={childId} />

      <ChildProgressCard childId={childId} />

      <SchoolIepsCard childId={childId} />

      <AboutMyChildCard childId={childId} childName={child.firstName} canEdit={canEdit} />

      <JournalCard childId={childId} childName={child.firstName} canEdit={canEdit} />

      {isOwner && (
        <Card data-testid="sharing-section">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-serif">{t("overview.sharingHeading")}</h2>
            <Button
              onClick={() => setShowShareDialog(true)}
              data-testid="share-invite-button"
            >
              <Share2
                className="w-4 h-4 mr-1.5"
                strokeWidth={1.8}
                aria-hidden="true"
              />
              {t("overview.inviteSomeone")}
            </Button>
          </div>
          <p className="text-sm text-brand-slate-500 mb-4">
            {t("overview.shareDescription", { name: child.firstName })}
          </p>
          <AccessList key={accessListKey} childId={childId} isOwner={isOwner} />

          <Modal
            open={showShareDialog}
            onClose={() => setShowShareDialog(false)}
            title={t("overview.shareModalTitle", { name: child.firstName })}
            data-testid="share-child-modal"
          >
            <ShareChildDialog
              childId={childId}
              onInvited={() => {
                setAccessListKey((k) => k + 1);
                setShowShareDialog(false);
              }}
              onCancel={() => setShowShareDialog(false)}
            />
          </Modal>
        </Card>
      )}

      {isOwner && (
        <Card>
          <div className="flex items-center justify-between gap-4">
            <div>
              <h2 className="font-serif">{t("overview.studentAccountHeading")}</h2>
              <p className="mt-1 max-w-prose text-sm text-brand-slate-500">
                {t("overview.inviteStudentDescription", { name: child.firstName })}
              </p>
            </div>
            <Button
              variant="secondary"
              className="shrink-0"
              onClick={() => setShowInviteStudent(true)}
              data-testid="invite-student-open"
            >
              {t("overview.inviteStudent")}
            </Button>
          </div>
        </Card>
      )}

      <Modal
        open={showInviteStudent}
        onClose={() => setShowInviteStudent(false)}
        title={t("overview.inviteStudent")}
        data-testid="invite-student-modal"
      >
        <InviteStudentForm
          embedded
          onInvite={async (email) => {
            const result = await handleInviteStudent(email);
            if (result.success) setShowInviteStudent(false);
            return result;
          }}
          description={t("overview.inviteStudentDescription", { name: child.firstName })}
        />
      </Modal>
    </div>
  );
}

function ProfileField({ label, value }: { label: string; value: string }) {
  return (
    <div className="bg-brand-slate-50 rounded-card p-3 border border-brand-slate-200">
      <p className="text-[11px] text-brand-slate-500 uppercase tracking-wide font-semibold">
        {label}
      </p>
      <p className="text-sm font-medium text-brand-slate-800 mt-1">{value}</p>
    </div>
  );
}
